/**
 * WarrantScreener — Round P140·3 (Feb 2026)
 *
 * Dedicated screener for the 441+ US stock warrants ingested by our
 * weekly Massive `type=WARRANT` cron. Kept OUT of the general Screener
 * because warrants share almost no columns with regular equities
 * (they have no yield, no dividend growth, no coverage — but they
 * DO have exercise price + underlying ticker + redemption status
 * that the standard screener has no concept of).
 *
 * Route: `/warrants`. The general `/screener?asset_type=warrant` URL
 * silently redirects here (see Screener.js effect) so the user-
 * requested URL shape still works.
 *
 * Default sort per user spec:
 *   1. status == "redeemed" (past events, freshest first)
 *   2. status == "redemption_announced" (upcoming, soonest first)
 *   3. everything else (active), alphabetical by ticker
 * Enforced server-side by the /api/warrants aggregation pipeline —
 * this component just renders whatever order the backend returns.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ExternalLink, Search, X } from 'lucide-react';
import LearnPage from '@/pages/learn/LearnPage';
import { WarrantBadge } from '@/components/WarrantBadge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import WatchlistStarButton from '@/components/WatchlistStarButton';
import WarrantBreakEvenPill from '@/components/WarrantBreakEvenPill';
import WarrantExpiryChip from '@/components/WarrantExpiryChip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WarrantScreener() {
  const [warrants, setWarrants] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [exchangeFilter, setExchangeFilter] = useState('');
  const [search, setSearch] = useState('');
  // Round P140·5 (Feb 2026) — Column sort. Default `sortKey=null` =
  // server's default (redeemed → announced → alphabetical); clicking
  // a header cycles null → asc → desc → null. Sorting is client-side
  // because the dataset is capped at ~500 rows.
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const toggleSort = (key) => {
    if (sortKey !== key) { setSortKey(key); setSortDir('asc'); return; }
    if (sortDir === 'asc') { setSortDir('desc'); return; }
    setSortKey(null); setSortDir('asc'); // third click clears
  };

  // Round P141·2 (Feb 2026) — Desktop horizontal-scroll chevrons.
  // The table has 9 columns and easily overflows on 1080p / 1440p
  // monitors, but the native scrollbar sits at the very bottom of the
  // page (below the 400+ rows). Users on trackpads can two-finger
  // swipe, but mouse users have no easy way to slide the columns.
  // These chevrons overlay the left/right edges of the scroll area
  // and shift by ~1 column-cell (120px) per click with smooth
  // scrolling so the motion is visible. Hold-to-repeat fires every
  // 60ms while the mouse button is down for continuous slide.
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      setCanScrollLeft(el.scrollLeft > 0);
      // -1 tolerance for sub-pixel rounding at max scroll.
      setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [warrants.length]);
  const nudgeScroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    // 120px per click ≈ one column-cell shift — small enough to feel
    // controlled, big enough to see the columns actually move.
    el.scrollBy({ left: dir * 120, behavior: 'smooth' });
  };
  // Hold-to-repeat: fires nudgeScroll every 150ms while the mouse
  // button is held down. 150ms pairs with the 120px smooth animation
  // so successive nudges chain into a continuous slide instead of
  // stuttering. Clears on mouseup / mouseleave.
  const holdIntervalRef = useRef(null);
  const startHold = (dir) => {
    nudgeScroll(dir);
    holdIntervalRef.current = setInterval(() => nudgeScroll(dir), 150);
  };
  const stopHold = () => {
    if (holdIntervalRef.current) {
      clearInterval(holdIntervalRef.current);
      holdIntervalRef.current = null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { limit: 1000 };
    if (statusFilter) params.status = statusFilter;
    if (exchangeFilter) params.exchange = exchangeFilter;
    axios
      .get(`${API}/warrants`, { params })
      .then(({ data }) => {
        if (cancelled) return;
        setWarrants(data?.warrants || []);
        setTotal(data?.total || 0);
      })
      .catch(() => {
        if (!cancelled) { setWarrants([]); setTotal(0); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statusFilter, exchangeFilter]);

  // Client-side search — dataset is capped at ~500 rows so instant
  // filter over ticker + name + underlying is fine (no debounce).
  const filtered = useMemo(() => {
    let out = warrants;
    if (search.trim()) {
      const q = search.trim().toUpperCase();
      out = out.filter(w =>
        (w.ticker || '').toUpperCase().includes(q) ||
        (w.underlying_ticker || '').toUpperCase().includes(q) ||
        (w.name || '').toUpperCase().includes(q)
      );
    }
    // Round P140·5 (Feb 2026) — Optional column sort. Preserves the
    // server's default status-priority order when sortKey is null so
    // fresh redemptions still float to the top of an unfiltered view.
    if (sortKey) {
      const dirMul = sortDir === 'asc' ? 1 : -1;
      const numericKeys = new Set(['exercise_price', 'redemption_price', '__distance_pct']);
      out = [...out].sort((a, b) => {
        // Special key: `__distance_pct` reads from heat_strip.
        let va = sortKey === '__distance_pct' ? a?.heat_strip?.distance_pct : a?.[sortKey];
        let vb = sortKey === '__distance_pct' ? b?.heat_strip?.distance_pct : b?.[sortKey];
        // Nulls always sink to the bottom regardless of direction.
        const aN = va == null || va === '';
        const bN = vb == null || vb === '';
        if (aN && bN) return 0;
        if (aN) return 1;
        if (bN) return -1;
        if (numericKeys.has(sortKey)) return (va - vb) * dirMul;
        return String(va).localeCompare(String(vb)) * dirMul;
      });
    }
    return out;
  }, [warrants, search, sortKey, sortDir]);

  // Round P140·4 (Feb 2026) — Clear + Undo. Mirrors the pattern in
  // /pages/Screener.js (line 1544): snapshot every filter BEFORE
  // resetting so the sonner toast's Undo action can restore them
  // exactly. Because our filters drive the `useEffect` above (search
  // via useMemo, status/exchange via the fetch effect), restoring the
  // state values automatically re-fetches and re-renders the table —
  // no manual refetch call needed.
  const activeCount = (search.trim() ? 1 : 0) + (statusFilter ? 1 : 0) + (exchangeFilter ? 1 : 0) + (sortKey ? 1 : 0);
  const clearFilters = () => {
    if (activeCount === 0) return; // nothing to clear
    const snapshot = { search, statusFilter, exchangeFilter, sortKey, sortDir };
    setSearch('');
    setStatusFilter('');
    setExchangeFilter('');
    setSortKey(null);
    setSortDir('asc');
    toast(`Cleared ${activeCount} filter${activeCount === 1 ? '' : 's'}`, {
      description: 'Warrant list reset.',
      duration: 7000,
      action: {
        label: 'Undo',
        onClick: () => {
          setSearch(snapshot.search);
          setStatusFilter(snapshot.statusFilter);
          setExchangeFilter(snapshot.exchangeFilter);
          setSortKey(snapshot.sortKey);
          setSortDir(snapshot.sortDir);
        },
      },
    });
  };

  return (
    <LearnPage
      title="Warrant Screener"
      description="Every U.S. stock warrant tracked by DiviDrip — with live redemption alerts via our weekly 8-K watcher."
      hideBackButton
      matchHeader
    >
      <div className="mb-4 flex items-center gap-3">
        <BackOrHomeButton escHintStacked testid="warrant-screener-back-btn" />
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground" data-testid="warrant-screener-title">
            Warrant Screener
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {loading ? 'Loading…' : `${total} warrants ingested from Massive · Refreshes every Saturday 8:00 AM ET`}
          </p>
          {!loading && (
            <p className="text-xs text-muted-foreground mt-0.5">
              8-K redemption watcher runs Saturday 8:00 AM ET
            </p>
          )}
        </div>
      </div>

      {/* Filter strip */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search ticker, name, underlying…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs w-56"
            data-testid="warrant-screener-search"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-8 text-xs rounded-md border border-border bg-background px-2"
          data-testid="warrant-screener-status-filter"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="redemption_announced">Redemption Announced</option>
          <option value="redeemed">Redeemed</option>
          <option value="expired">Expired</option>
        </select>
        <select
          value={exchangeFilter}
          onChange={(e) => setExchangeFilter(e.target.value)}
          className="h-8 text-xs rounded-md border border-border bg-background px-2"
          data-testid="warrant-screener-exchange-filter"
        >
          <option value="">All exchanges</option>
          <option value="XNAS">Nasdaq (XNAS)</option>
          <option value="XNYS">NYSE (XNYS)</option>
          <option value="XASE">AMEX (XASE)</option>
        </select>
        {/* Round P140·4 — Clear button + Undo toast. Only enabled when
            at least one filter is active. Undo restores the snapshot
            and the useEffect / useMemo above re-derives the visible
            rows automatically. */}
        <Button
          variant="outline"
          size="sm"
          onClick={clearFilters}
          disabled={activeCount === 0}
          className="h-8 gap-1 text-xs"
          data-testid="warrant-screener-clear-btn"
        >
          <X className="w-3 h-3" />
          Clear
          {activeCount > 0 && (
            <span className="ml-0.5 text-[10px] font-mono opacity-70">({activeCount})</span>
          )}
        </Button>
        <div className="ml-auto text-[11px] text-muted-foreground">
          Showing <span className="font-mono font-semibold text-foreground">{filtered.length}</span> of {total}
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground italic py-8 text-center">
          No warrants match the current filters.
        </p>
      )}

      {!loading && filtered.length > 0 && (
        <div className="flex items-stretch gap-0">
          {/* Round P141·4 (Feb 2026) — Left chevron column. Sits FLUSH
              against the left edge of the scrollable table column so
              the flat side of the triangle abuts the table border.
              `sticky top-1/2` keeps the triangle vertically centered
              between the table top and the viewport bottom while the
              user scrolls the page. Only rendered on lg: up (mobile
              users swipe). Auto-hidden when scrollLeft === 0 so it
              doesn't linger uselessly. */}
          <div className="hidden lg:block w-6 flex-shrink-0 relative">
            {canScrollLeft && (
              <button
                type="button"
                onMouseDown={() => startHold(-1)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                onTouchStart={() => startHold(-1)}
                onTouchEnd={stopHold}
                aria-label="Scroll table left"
                title="Scroll left (hold to repeat)"
                data-testid="warrant-screener-scroll-left"
                className="sticky top-[50vh] -translate-y-1/2 inline-flex items-center justify-center w-6 h-10 group focus:outline-none"
              >
                {/* Solid left-pointing triangle drawn as SVG polygon.
                    Flat side (right edge) lines up with the table's
                    left border. */}
                <svg viewBox="0 0 12 20" className="w-4 h-8 fill-violet-600 group-hover:fill-violet-500 drop-shadow" aria-hidden="true">
                  <polygon points="12,0 0,10 12,20" />
                </svg>
              </button>
            )}
          </div>

          <div
            ref={scrollRef}
            className="overflow-x-auto rounded-lg border border-border/60 flex-1 min-w-0"
            data-testid="warrant-screener-scroll-wrapper"
          >
            <table className="w-full text-sm" data-testid="warrant-screener-table">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                {[
                  { key: null,                  label: '★',                align: 'center' },
                  { key: 'ticker',              label: 'Ticker',           align: 'left'  },
                  { key: 'status',              label: 'Status',           align: 'left'  },
                  { key: 'underlying_ticker',   label: 'Underlying',       align: 'left'  },
                  { key: 'exercise_price',      label: 'Exercise',         align: 'right' },
                  { key: '__distance_pct',      label: 'To Break-Even',    align: 'center' },
                  { key: 'expiration_date',     label: 'Expires',          align: 'center' },
                  { key: 'primary_exchange',    label: 'Exchange',         align: 'left'  },
                  { key: 'name',                label: 'Name',             align: 'left'  },
                  { key: null,                  label: 'SEC',              align: 'center' },
                ].map((col) => {
                  const active = sortKey === col.key && col.key != null;
                  const arrow = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
                  return (
                    <th
                      key={col.label}
                      className={`px-2 py-2 text-${col.align} font-semibold ${col.key ? 'cursor-pointer select-none hover:text-foreground' : ''} ${active ? 'text-foreground' : ''}`}
                      onClick={() => col.key && toggleSort(col.key)}
                      data-testid={col.key ? `warrant-sort-${col.key}` : undefined}
                    >
                      {col.label}{arrow}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filtered.map((w, i) => (
                <tr
                  key={`${w.ticker}-${i}`}
                  className="border-t border-border/40 hover:bg-muted/20 transition-colors"
                  data-testid={`warrant-row-${w.ticker}`}
                >
                  <td className="px-2 py-1.5 text-center align-middle">
                    <WatchlistStarButton ticker={w.ticker} />
                  </td>
                  <td className="px-2 py-1.5 font-mono font-semibold">
                    <Link
                      to={`/stock/${encodeURIComponent(w.ticker)}`}
                      className="text-blue-600 dark:text-cyan-400 hover:underline"
                    >
                      {w.ticker}
                    </Link>
                  </td>
                  <td className="px-2 py-1.5">
                    <WarrantBadge
                      status={w.status}
                      deadline={w.redemption_deadline}
                      price={w.redemption_price}
                      testIdSuffix={`row-${w.ticker}`}
                    />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs">
                    {w.underlying_ticker ? (
                      <Link
                        to={`/stock/${encodeURIComponent(w.underlying_ticker)}`}
                        className="text-blue-600 dark:text-cyan-400 hover:underline"
                      >
                        {w.underlying_ticker}
                      </Link>
                    ) : '—'}
                  </td>
                  <td className="px-2 py-1.5 font-mono text-xs text-right">
                    {w.exercise_price != null ? `$${w.exercise_price.toFixed(2)}` : '—'}
                  </td>
                  {/* Round P140·6 (Feb 2026) — Break-even heat strip.
                      distance_pct convention: +N = underlying must
                      RISE N% to break even (red/out of money),
                      0 = at break-even (amber),
                      -N = underlying is above break-even (green/ITM). */}
                  <td className="px-2 py-1.5 text-center">
                    <WarrantBreakEvenPill warrant={w} />
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <WarrantExpiryChip expirationDate={w.expiration_date} />
                  </td>
                  <td className="px-2 py-1.5 font-mono text-[11px]">{w.primary_exchange || '—'}</td>
                  <td className="px-2 py-1.5 text-xs text-muted-foreground max-w-md truncate" title={w.name}>
                    {w.name || '—'}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    {w.cik ? (
                      <a
                        href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${w.cik}&type=8-K`}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex items-center text-blue-600 dark:text-cyan-400 hover:underline"
                        title="View SEC 8-K filings"
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground/60">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Round P141·4 (Feb 2026) — Right chevron column. Mirrors
              the left — solid violet triangle pointing right, flat
              side flush against the right edge of the table. */}
          <div className="hidden lg:block w-6 flex-shrink-0 relative">
            {canScrollRight && (
              <button
                type="button"
                onMouseDown={() => startHold(1)}
                onMouseUp={stopHold}
                onMouseLeave={stopHold}
                onTouchStart={() => startHold(1)}
                onTouchEnd={stopHold}
                aria-label="Scroll table right"
                title="Scroll right (hold to repeat)"
                data-testid="warrant-screener-scroll-right"
                className="sticky top-[50vh] -translate-y-1/2 inline-flex items-center justify-center w-6 h-10 group focus:outline-none"
              >
                <svg viewBox="0 0 12 20" className="w-4 h-8 fill-violet-600 group-hover:fill-violet-500 drop-shadow" aria-hidden="true">
                  <polygon points="0,0 12,10 0,20" />
                </svg>
              </button>
            )}
          </div>
        </div>
      )}

      <p className="mt-4 text-[11px] text-muted-foreground leading-snug italic">
        Data source: Massive.com <code className="font-mono">/v3/reference/tickers?type=WARRANT</code>.
        Redemption events are detected weekly from SEC 8-K filings (Items 3.03 + 8.01) via Massive&rsquo;s disclosures API.
        Warrants pay NO dividends and CAN be force-redeemed at ~$0.01 with 30 days notice — always exercise or sell before the deadline.
      </p>
    </LearnPage>
  );
}
